import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckoutComFramesReady,
  EncryptedPubKey,
  ExchangesFees,
  PaymentContext,
  PaymentInfoContext,
  PaymentMode,
  WalletItems
} from '../../App';
import axios from '../../api/axios';
import Routers from '../../routers';
import Loader from '../../components/Loader';
import { PaymentInfoMapper } from '../../models/mapper/paymentInfoMapper';
import PayNowContainer from '../PayNowContainer';
import i18next from 'i18next';
import { PaymentModes } from '../../utils';
import { UK_POSTAL_CODE, US_ZIP_CODE } from "../../constants";
import Frames from "../CheckoutComPayContainer";
import {checkoutComEvents} from "../../events/checkoutCom.events";
import { useTranslation } from 'react-i18next';
import MetadataUpdater from '../../components/MetadataUpdater';

export const ProviderTypeContext = React.createContext(null);
export const PaymentAccountPubKeyContext = React.createContext(null);
const InitCheckoutContainer = () => {
  const payment = useContext(PaymentContext);
  const paymentMode = useContext(PaymentMode);
  const [loading, setLoading] = useState(false);
  const [paymentAccountPubKey, setPaymentAccountPubKey] = useState();
  const [providerType, setProviderType] = useState();
  const [isFramesElementsReady, setIsFramesElementsReady] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState();
  const [encryptedPubKey, setEncryptedPubKey] = useState();
  const [searchParams] = useSearchParams();
  const [exchangeFees, setExchangeFees] = useState();
  const [walletItems, setWalletItems] = useState();
  const { t } = useTranslation();

  const cardExpiryPlaceholder = {month: 'MM', year: 'YY'};

  const isPaymentInfoReady = useMemo(() => {
    return paymentInfo && paymentInfo.merchantPubKey;
  }, [paymentInfo]);

  useEffect(() => {
    if (payment) {
      (async () => {
        await getPaymentIntent();
      })();
    }
  }, [payment]);

  useEffect(() => {
    const key = searchParams.get('key');
    if (key) {
      setEncryptedPubKey(key);
      (async () => {
        await getPaymentInfo(key);
      })();
    }
  }, []);

  const updateCardExpiry = () => {
    if (!t('card_expiry_placeholder'))
      return;
    const split = t('card_expiry_placeholder').split('/');
    if (split.length != 2)
      return;

    cardExpiryPlaceholder.month = split[0];
    cardExpiryPlaceholder.year = split[1];
  }  

  const getPaymentIntent = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post('payment', payment, {
        headers: {
          'X-UserKey': payment?.userKey
        }
      });
      await getPaymentInfo(data.encryptedPubKey);
      setEncryptedPubKey(data.encryptedPubKey);
      setLoading(false);
    } catch (err) {
      parent.postMessage(
        {
          type: 'ON_FORM_LOAD_ERROR',
          value: 'Payment post API Failed'
        },
        '*'
      );
      setLoading(false);
    }
  };
  const getSumOfSameSchedule = (data) => {
    var result = Object.values(
      data.reduce((obj, item) => {
        var key = item.dateTime;
        if (!obj[key]) {
          obj[key] = Object.assign(item);
        } else {
          obj[key].amountInOrderCurrency += item.amountInOrderCurrency;
        }
        return obj;
      }, {})
    );
    result?.sort(function(a, b) { return new Date(a.dateTime) - new Date(b.dateTime) });
    return result;
  };

  useEffect(() => {
    const unsubscribePaymentCreated = checkoutComEvents.on("paymentCreated", (e) => {
      const key = searchParams.get('key') || encryptedPubKey || e;

      if(!key) return;

      getPaymentInfo(key);
    });

    const unsubscribeReinit = checkoutComEvents.on("reInitialize", (e) => {
      if (window.Frames && typeof window.Frames.init === 'function') {
        window.Frames.init();
      }
    });

    return () => {
      unsubscribePaymentCreated();
      unsubscribeReinit();
    }
  }, []);

  const getPaymentInfo = async (encryptedPubKey) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/payment-info/${encryptedPubKey}`);
      i18next.changeLanguage(data?.language ? data.language.toLowerCase() : 'en');
      if (data?.paymentSchedule?.length) {
        data.paymentSchedule = getSumOfSameSchedule(data?.paymentSchedule);
      }

      const mappedData = PaymentInfoMapper.toInfoPayment(data)

      setProviderType(mappedData.providerType);
      setPaymentAccountPubKey(mappedData.paymentAccountPubKey);

      setPaymentInfo(mappedData);
      await getWalletItems();
      if(data){
        parent.postMessage(
          {
            type: 'ON_FORM_LOAD_SUCCESS',
            value: 'ON_FORM_LOAD_SUCCESS'
          },
          '*'
        );
      }
    } catch (err) {
      setPaymentInfo(PaymentInfoMapper.toErrorPayment(err?.response?.data));
      parent.postMessage(
        {
          type: 'ON_FORM_LOAD_ERROR',
          value: 'Get Payment-info API has been Failed'
        },
        '*'
      );
      setLoading(false);
    }
    setLoading(false);
  };

  const getWalletItems = async () => {
    if (!payment || !payment.customerId || !payment.customerSignature)
      return;
    try {
      const { data } = await axios.get('wallet-items/public', {
        headers: {
          'X-UserKey': payment?.userKey,
          'X-CustomerKey': payment?.customerId,
          'X-CustomerSignature': payment?.customerSignature,
        }
      });

      const validWalletItems = data.filter(({paymentMethod: { card: item }}) => {
        const errors = {}

        if ((item.country === 'US' || item.country === 'GB') && paymentMode!=PaymentModes.Token && !item.zip?.match(item.country === 'US' ? US_ZIP_CODE : UK_POSTAL_CODE)) {
            errors.zip = true
        }

        if ((item.country === 'US' || item.country === 'GB') && paymentMode!=PaymentModes.Token && !item.zip) {
            errors.zip = true
        }

        if ((item.country === 'US' || item.country === 'GB') && paymentMode!=PaymentModes.Token) {
          if (item.zip && !errors?.zip) {
            return true
          } else {
            return false
          }
        }
        else {
          return true
        }
      })

      setWalletItems(validWalletItems);
    } catch (error) {
      window.postMessage(
        {
          type: 'ON_ERROR',
          value: 'Error on Wallet Items From API Failed'
        },
        '*'
      );
    }
  }

  useEffect(() => {
    if (paymentInfo) {
      (async () => {
        await getExchangeFees(paymentInfo.currency);
      })();
    }
  }, [paymentInfo]);

  const getExchangeFees = async (currency) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`exchange-rates?currencyFrom=${currency}`, {
        headers: {
          'X-ApiKey': paymentInfo.merchantPubKey
        }
      });
      setExchangeFees(data?.items);
      setLoading(false);
    } catch (err) {
      window.postMessage(
        {
          type: 'ON_ERROR',
          value: 'Error on Exchange Rate currency From API Failed'
        },
        '*'
      );
      console.error(err);
      setLoading(false);
    }
  };

  const {field: fieldStyles = {}} = JSON.parse(paymentInfo?.jsonStyle || '{}');
  
  updateCardExpiry();
  return (
    <PaymentInfoContext.Provider value={paymentInfo}>
      <PaymentAccountPubKeyContext.Provider value={{paymentAccountPubKey, setPaymentAccountPubKey}}>
        <EncryptedPubKey.Provider value={encryptedPubKey}>
          <ExchangesFees.Provider value={exchangeFees}>
            <WalletItems.Provider value={walletItems}>
              <ProviderTypeContext.Provider value={{providerType, setProviderType}}>
                <PaymentMode.Provider value={paymentInfo?.type}>
                  <MetadataUpdater/>
                  <Loader loading={loading} />              
                    <PayNowContainer>
                      <CheckoutComFramesReady.Provider value={(v) => {
                        setIsFramesElementsReady(v);
                      }}>
                        <Frames
                            isFramesElementsReady={isFramesElementsReady && isPaymentInfoReady}
                            config={{
                              debug: false,
                              publicKey: paymentAccountPubKey,
                              frameActivated: false,
                              localization: {
                                cardNumberPlaceholder: '1234 1234 1234 1234',
                                expiryMonthPlaceholder: cardExpiryPlaceholder.month,
                                expiryYearPlaceholder: cardExpiryPlaceholder.year,
                                cvvPlaceholder: 'CVC',
                              },
                              style: {
                                base: {
                                  fontSize: '13px',
                                  fontFamily: 'Helvetica, Arial, sans-serif',
                                  lineHeight: '15.4388px',
                                  color: '#000',
                                  letterSpacing: 'normal',
                                  padding: '6px 10px',
                                  height: '100%',
                                  ...(fieldStyles && fieldStyles),
                                  '::placeholder': {
                                    color: '#707070',
                                  },
                                },
                                invalid: {
                                  iconColor: '#d10244',
                                  color: '#d10244',
                                },
                              },
                            }}
                            ready={() => {
                              checkoutComEvents.emit('ready');
                            }}
                            frameActivated={(e) => {
                              checkoutComEvents.emit('frameActivated', e);
                            }}
                            frameFocus={(e) => {
                              checkoutComEvents.emit('frameFocus', e);
                            }}
                            frameBlur={(e) => {
                              checkoutComEvents.emit('frameBlur', e);
                            }}
                            frameValidationChanged={(e) => {
                              checkoutComEvents.emit('frameValidationChanged', e);
                            }}
                            onValidationChanged={(e) => {
                              checkoutComEvents.emit('onValidationChanged', e);
                            }}
                            paymentMethodChanged={(e) => {
                              checkoutComEvents.emit('paymentMethodChanged', e)
                            }}
                            cardValidationChanged={(e) => {
                              checkoutComEvents.emit('cardValidationChanged', e);
                            }}
                            cardSubmitted={(e) => {
                              checkoutComEvents.emit('cardSubmitted', e);
                            }}
                            cardTokenized={(e) => {
                              checkoutComEvents.emit('cardTokenized', e);
                            }}
                            cardTokenizationFailed={(e) => {
                              checkoutComEvents.emit('cardTokenizationFailed', e);
                            }}
                            cardBinChanged={(e) => {
                              checkoutComEvents.emit('cardBinChanged', e);
                            }}
                        >
                          <Routers />
                        </Frames>
                      </CheckoutComFramesReady.Provider>
                    </PayNowContainer>
                </PaymentMode.Provider>
              </ProviderTypeContext.Provider>
            </WalletItems.Provider>
          </ExchangesFees.Provider>
        </EncryptedPubKey.Provider>
      </PaymentAccountPubKeyContext.Provider>
    </PaymentInfoContext.Provider>
  );
};

export default InitCheckoutContainer;
